import { useContext, useState } from "react";
import { Box, Typography, Button, Avatar, AvatarGroup, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";

import { AppContext } from "../../Context/AppContext";
import CustomTooltip from "../../Components/CustomTooltip";
import CustomFilterTabs from "../../Components/CustomFilterTabs";
import StatCard from "./components/StatCard";
import TopFiveTable from "./components/TopFiveTable";
import SetupCard from "./components/SetupCard";
import Sparkline from "./components/Sparkline";
import HealthStat from "./components/HealthStat";
import { useCommunityDashboard } from "./hooks/useCommunityDashboard";
import { bidiIsolate, formatMoney, formatPercent } from "../../Helpers/formating";
import {
  dashboardStyles as d,
  statCardStyles as sc,
  topFiveStyles as tf,
} from "./styles/styles";

const fmt = (n) => Number(n).toLocaleString("en-US");
const listTitleIcon = { fontSize: 18, color: "rgba(47, 43, 61, 0.55)" };

// A metric is null when there are no orders in that window — render a dash
// rather than a confident "0%", which would read as a failing score.
const NO_DATA = "\u2014";

const loadingBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
};

const TONES = {
  neutral: { color: "rgba(62, 63, 76, 0.7)", bg: "rgba(242, 243, 251, 1)" },
  good: { color: "rgba(36, 139, 112, 1)", bg: "rgba(219, 240, 236, 1)" },
  warn: { color: "rgba(180, 120, 20, 1)", bg: "rgba(255, 244, 229, 1)" },
  bad: { color: "rgba(200, 55, 55, 1)", bg: "rgba(252, 235, 235, 1)" },
};
const tier = (value, good, warn, higherBetter) => {
  // No data earns no colour — a missing metric is not a bad one.
  if (value === null || value === undefined) return "neutral";
  if (higherBetter) return value >= good ? "good" : value >= warn ? "warn" : "bad";
  return value <= good ? "good" : value <= warn ? "warn" : "bad";
};

const DashboardContainer = () => {
  const { t, i18n } = useTranslation("page-dashboard");
  const [qualityType, setQualityType] = useState("services");
  const { sidebarData } = useContext(AppContext);
  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";
  const loc = (v) =>
    v && typeof v === "object" && !Array.isArray(v) ? v[lang] ?? v.en : v;
  const locPoints = (pts) =>
    pts.map((p) => ({ value: p.value, label: loc(p.label), full: loc(p.full) }));
  const communityName = sidebarData?.communityName || "";
  const communityImage = sidebarData?.communityImage || "";
  const { data: m, isLoading, error } = useCommunityDashboard();

  if (isLoading || (!m && !error)) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <CircularProgress sx={{ color: "rgba(36, 139, 112, 1)" }} />
      </Box>
    );
  }

  if (error || !m) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <Typography sx={{ color: "rgba(62, 63, 76, 0.7)" }}>
          {t("load_error")}
        </Typography>
      </Box>
    );
  }

  const cur = m.currencySymbol || "₪";
  // Shared rule: ₪ first, thousands separators, decimals only with agorot.
  const money = (n) => formatMoney(n ?? 0, cur);
  // Nothing to withdraw at ₪0 (or no data). With a positive balance the button
  // is enabled but still has no action — payouts need a ledger first.
  const canWithdraw = m.managerEarnings.currentBalance > 0;
  const withdrawBtn = (
    <Button sx={sc.withdrawBtn} variant="contained" disableElevation disabled={!canWithdraw}>
      {t("withdraw")}
    </Button>
  );

  // A community with no transactions yet divides by zero — show 0, not NaN.
  const avg = (total, count) => (count ? Math.round(total / count) : 0);
  const avgAll = avg(m.transactions.moneyAllTime, m.transactions.countAllTime);
  const avgMonth = avg(m.transactions.moneyThisMonth, m.transactions.countThisMonth);

  const infoTip = (text) => (
    <CustomTooltip title={text}>
      <Box component="span" sx={sc.tooltipTrigger}>
        <Box
          component="img"
          src="/icons/question-circle-icon.svg"
          alt=""
          sx={sc.tooltipIcon}
        />
      </Box>
    </CustomTooltip>
  );

  const sectionHeader = (title, tip) => (
    <Box sx={d.sectionHeader}>
      <Typography sx={d.sectionTitle}>{title}</Typography>
      {tip ? infoTip(tip) : null}
    </Box>
  );

  const nameCell = (item, index) => (
    <Box sx={tf.nameCell}>
      <Box component="span" sx={tf.rank}>
        {index + 1}
      </Box>
      <Box sx={tf.avatar}>
        {item.image ? (
          <Box component="img" src={item.image} alt="" sx={tf.avatarImg} />
        ) : (
          loc(item.name).charAt(0)
        )}
      </Box>
      <span>{loc(item.name)}</span>
    </Box>
  );

  const toned = (text, level) => (
    <Box component="span" sx={{ color: TONES[level].color, fontWeight: 500 }}>
      {text}
    </Box>
  );

  const transactionsCol = {
    label: t("cols.transactions"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.count}>
        {fmt(row.count)}
      </Box>
    ),
  };
  const revenueCol = {
    label: t("cols.revenue"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.money}>
        {money(row.revenue)}
      </Box>
    ),
  };
  const categoryColumns = [
    { label: t("cols.category"), render: (row, i) => nameCell(row, i) },
    transactionsCol,
    revenueCol,
  ];
  const serviceColumns = [
    { label: t("cols.service"), render: (row, i) => nameCell(row, i) },
    transactionsCol,
    revenueCol,
  ];
  const supplierColumns = [
    { label: t("cols.supplier"), render: (row, i) => nameCell(row, i) },
    revenueCol,
  ];

  const isServices = qualityType === "services";
  const q = m.quality[qualityType];
  // Durations read in the unit people think in: over 48 hours shows as
  // "about N days", and under a day as "less than a day" (never "0 days").
  // Percentages are whole numbers on this summary screen.
  const fmtUnit = (v, unit) => {
    if (v === null || v === undefined) return NO_DATA;
    if (unit === "hours") {
      if (v > 48) return t("sq.about_days", { count: Math.round(v / 24) });
      return t("sq.hours_value", { count: v });
    }
    if (unit === "days") {
      if (v < 1) return t("sq.less_than_day");
      return t("sq.days_value", { count: v });
    }
    return formatPercent(v);
  };
  const thisWeekPill = (n) =>
    n ? (
      <Box component="span" sx={sc.deltaPill}>
        <bdi>+{fmt(n)}</bdi> {t("this_week")}
      </Box>
    ) : null;
  const metricConfig = [
    { key: "fulfillment", unit: "percent", labelKey: "sq.fulfillment_rate", tipKey: "sq.tips.fulfillment", good: 90, warn: 75, higherBetter: true },
    { key: "approval", unit: "hours", labelKey: isServices ? "sq.avg_response" : "sq.avg_approval", tipKey: isServices ? "sq.tips.response" : "sq.tips.approval", good: 4, warn: 12, higherBetter: false },
    { key: "completion", unit: "days", labelKey: isServices ? "sq.avg_completion" : "sq.avg_delivery", tipKey: isServices ? "sq.tips.completion" : "sq.tips.delivery", good: 2, warn: 4, higherBetter: false },
    { key: "cancellation", unit: "percent", labelKey: "sq.cancellation_rate", tipKey: "sq.tips.cancellation", good: 5, warn: 12, higherBetter: false },
  ];
  const healthMetrics = metricConfig.map((c) => {
    const md = q.metrics[c.key];
    return {
      label: t(c.labelKey),
      value: fmtUnit(md.raw, c.unit),
      level: tier(md.raw, c.good, c.warn, c.higherBetter),
      tip: t(c.tipKey),
      trend: md.trend.map(([month, v]) => [loc(month), fmtUnit(v, c.unit)]),
    };
  });
  const qualityColumns = [
    { label: t("cols.supplier"), render: (row, i) => nameCell(row, i) },
    { label: t("cols.fulfillment"), align: "right", render: (row) => toned(formatPercent(row.fulfillment), tier(row.fulfillment, 90, 75, true)) },
    { label: t(isServices ? "cols.response" : "cols.approval"), align: "right", render: (row) => (
      <Box component="span" sx={tf.count}>
        {fmtUnit(row.approvalHours, "hours")}
      </Box>
    ) },
    { label: t("cols.cancellations"), align: "right", render: (row) => toned(formatPercent(row.cancellationRate), tier(row.cancellationRate, 5, 12, false)) },
  ];

  return (
    <Box sx={d.page}>
      <Box sx={d.header}>
        <Avatar src={communityImage} alt={communityName} sx={d.headerLogo}>
          {communityName.charAt(0)}
        </Avatar>
        <Typography sx={d.title}>
          {communityName
            ? t("page_title", { name: bidiIsolate(communityName) })
            : t("page_title_fallback")}
        </Typography>
      </Box>

      <Box sx={d.chartGrid}>
        <StatCard
          icon={<SwapHorizIcon sx={{ color: "#7367F0", fontSize: 24 }} />}
          iconBg="rgba(242, 243, 251, 1)"
          label={t("cards.transactions")}
          tooltip={infoTip(t("tooltips.transactions"))}
          hideIconOnMobile
        >
          <Typography sx={sc.amount}>{money(m.transactions.moneyAllTime)}</Typography>
          <Typography sx={sc.subline}>
            {t("tx_alltime", {
              count: Number(m.transactions.countAllTime) || 0,
              formatted: fmt(m.transactions.countAllTime),
              avg: money(avgAll),
            })}
          </Typography>
          <Typography sx={sc.subline}>
            {t("tx_month", {
              money: money(m.transactions.moneyThisMonth),
              count: Number(m.transactions.countThisMonth) || 0,
              formatted: fmt(m.transactions.countThisMonth),
              avg: money(avgMonth),
            })}
          </Typography>
          <Sparkline
            points={locPoints(m.transactions.monthly)}
            color="#7367F0"
            fill="rgba(115, 103, 240, 0.12)"
            formatValue={money}
          />
        </StatCard>

        <StatCard
          icon={
            <PaymentsOutlinedIcon
              sx={{ color: "rgba(36, 139, 112, 1)", fontSize: 24 }}
            />
          }
          iconBg="rgba(219, 240, 236, 1)"
          label={t("cards.earnings")}
          tooltip={infoTip(t("tooltips.earnings"))}
          hideIconOnMobile
          headerRight={
            <Box sx={sc.walletTop}>
              <Box sx={sc.balanceBox}>
                <Typography sx={sc.balanceLabel}>
                  {t("current_balance")}
                </Typography>
                <Typography sx={sc.balanceValue}>
                  {m.managerEarnings.currentBalance === null
                    ? NO_DATA
                    : money(m.managerEarnings.currentBalance)}
                </Typography>
              </Box>
              {canWithdraw ? withdrawBtn : (
                <CustomTooltip title={t("withdraw_unavailable_tip")}>
                  <Box component="span" sx={sc.withdrawTipTrigger}>
                    {withdrawBtn}
                  </Box>
                </CustomTooltip>
              )}
            </Box>
          }
        >
          <Typography sx={sc.amount}>{money(m.managerEarnings.allTime)}</Typography>
          <Typography sx={sc.subline}>{t("earn_alltime")}</Typography>
          <Typography sx={sc.subline}>
            {t("earn_month", { money: money(m.managerEarnings.thisMonth) })}
          </Typography>
          <Sparkline
            points={locPoints(m.managerEarnings.monthly)}
            color="rgba(36, 139, 112, 1)"
            fill="rgba(36, 139, 112, 0.12)"
            formatValue={money}
          />
        </StatCard>
      </Box>

      {sectionHeader(t("sections.top"))}
      <Box sx={d.topGrid}>
        <TopFiveTable
          emptyText={t("sq.no_data")}
          icon={<CategoryOutlinedIcon sx={listTitleIcon} />}
          title={t("top.categories")}
          tooltip={infoTip(t("top.tips.categories"))}
          columns={categoryColumns}
          rows={m.topProductCategories}
        />
        <TopFiveTable
          emptyText={t("sq.no_data")}
          icon={<ReceiptLongOutlinedIcon sx={listTitleIcon} />}
          title={t("top.pricelists")}
          tooltip={infoTip(t("top.tips.pricelists"))}
          columns={serviceColumns}
          rows={m.topServicePricelists}
        />
        <TopFiveTable
          emptyText={t("sq.no_data")}
          icon={<StorefrontOutlinedIcon sx={listTitleIcon} />}
          title={t("top.suppliers")}
          tooltip={infoTip(t("top.tips.suppliers"))}
          columns={supplierColumns}
          rows={m.topSuppliers}
        />
      </Box>

      <Box sx={d.qualityHeaderRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={d.sectionTitle}>{t("sections.quality")}</Typography>
          {infoTip(t("sections.quality_tip"))}
        </Box>
        <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
          <CustomFilterTabs
            tabs={[
              { label: t("sq.tab_services"), value: "services" },
              { label: t("sq.tab_stores"), value: "products" },
              { label: t("sq.tab_restaurants"), value: "restaurants", disabled: true, soonBadge: true },
              { label: t("sq.tab_coupons"), value: "coupons", disabled: true, soonBadge: true },
            ]}
            activeTab={qualityType}
            onChange={(_, v) => setQualityType(v)}
          />
        </Box>
      </Box>
      <Box sx={d.qualityGrid}>
        <Box sx={d.healthGrid}>
          {healthMetrics.map((h, i) => (
            <HealthStat
              key={i}
              label={h.label}
              value={h.value}
              tone={TONES[h.level]}
              // No data earns no pill. `sq.status` only defines good/warn/bad —
              // a "neutral" lookup would render the raw key next to a "—".
              statusText={h.level === "neutral" ? null : t(`sq.status.${h.level}`)}
              tooltip={infoTip(h.tip)}
              trend={h.trend}
            />
          ))}
        </Box>
        <TopFiveTable
          emptyText={t("sq.no_data")}
          icon={<SpeedOutlinedIcon sx={listTitleIcon} />}
          title={t("sq.supplier_table_title")}
          tooltip={infoTip(t("sq.supplier_table_tip"))}
          columns={qualityColumns}
          rows={q.suppliers}
        />
      </Box>

      {sectionHeader(t("sections.community"))}
      <Box sx={d.midGrid}>
        <StatCard
          icon={<GroupsOutlinedIcon sx={{ color: "#ff9f43", fontSize: 24 }} />}
          iconBg="rgba(255, 244, 229, 1)"
          label={t("cards.community")}
          tooltip={infoTip(t("tooltips.community"))}
        >
          <Box sx={sc.twoStat}>
            <Box sx={sc.commStat}>
              <AvatarGroup max={4} sx={sc.avatarGroup}>
                {m.community.memberAvatars.map((src, i) => (
                  <Avatar key={i} src={src} alt="" />
                ))}
              </AvatarGroup>
              <Typography sx={sc.statNum}>{fmt(m.community.members)}</Typography>
              <Typography sx={sc.statCaption}>
                {t("members")}
                {thisWeekPill(m.community.membersThisWeek)}
              </Typography>
            </Box>
            <Box sx={sc.commStat}>
              <AvatarGroup max={4} sx={sc.avatarGroup}>
                {m.community.supplierAvatars.map((src, i) => (
                  <Avatar key={i} src={src} alt="" />
                ))}
              </AvatarGroup>
              <Typography sx={sc.statNum}>
                {fmt(m.community.suppliers)}
              </Typography>
              <Typography sx={sc.statCaption}>
                {t("suppliers")}
                {thisWeekPill(m.community.suppliersThisWeek)}
              </Typography>
            </Box>
          </Box>
        </StatCard>

        <SetupCard />
      </Box>
    </Box>
  );
};

export default DashboardContainer;
